/**
 * Tooltip - Reusable tooltip component for the Vettis dashboard
 *
 * Features:
 * - Hover (desktop) and tap (mobile) activation
 * - Portal-based rendering to avoid clipping in scrollable containers
 * - Smooth framer-motion animations
 * - Configurable positioning with auto-flip
 * - Keyboard accessible (Escape to dismiss)
 * - InfoTooltip convenience wrapper for the common "info icon + tooltip" pattern
 */

import { useState, useRef, useEffect, useCallback, useId, cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  /** Content displayed inside the tooltip bubble */
  content: React.ReactNode;
  /** Element that triggers the tooltip */
  children: React.ReactElement;
  /** Preferred position relative to trigger */
  position?: TooltipPosition;
  /** Delay before showing on hover (ms) */
  delay?: number;
  /** Maximum width of tooltip */
  maxWidth?: number;
  /** Additional className for the tooltip bubble */
  className?: string;
  /** Disable the tooltip */
  disabled?: boolean;
}

interface InfoTooltipProps {
  /** Content displayed inside the tooltip */
  content: React.ReactNode;
  /** Preferred position */
  position?: TooltipPosition;
  /** Icon size */
  size?: 'sm' | 'md';
  /** Additional className for the icon */
  className?: string;
}

const ARROW_SIZE = 6;
const OFFSET = 8;

function getTooltipPosition(
  triggerRect: DOMRect,
  tooltipRect: { width: number; height: number },
  preferred: TooltipPosition
): { top: number; left: number; actual: TooltipPosition } {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const positions: Record<TooltipPosition, { top: number; left: number }> = {
    top: {
      top: triggerRect.top + scrollY - tooltipRect.height - OFFSET - ARROW_SIZE,
      left: triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2,
    },
    bottom: {
      top: triggerRect.bottom + scrollY + OFFSET + ARROW_SIZE,
      left: triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2,
    },
    left: {
      top: triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2,
      left: triggerRect.left + scrollX - tooltipRect.width - OFFSET - ARROW_SIZE,
    },
    right: {
      top: triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2,
      left: triggerRect.right + scrollX + OFFSET + ARROW_SIZE,
    },
  };

  // Try preferred position first, then fallback
  const fallbackOrder: Record<TooltipPosition, TooltipPosition[]> = {
    top: ['top', 'bottom', 'right', 'left'],
    bottom: ['bottom', 'top', 'right', 'left'],
    left: ['left', 'right', 'top', 'bottom'],
    right: ['right', 'left', 'top', 'bottom'],
  };

  for (const pos of fallbackOrder[preferred]) {
    const { top, left } = positions[pos];
    const fitsX = left >= 8 && left + tooltipRect.width <= vw - 8;
    const fitsY = top - scrollY >= 8 && top - scrollY + tooltipRect.height <= vh - 8;
    if (fitsX && fitsY) {
      return { top, left, actual: pos };
    }
  }

  // If nothing fits perfectly, clamp preferred position to viewport
  const { top, left } = positions[preferred];
  return {
    top: Math.max(scrollY + 8, Math.min(top, scrollY + vh - tooltipRect.height - 8)),
    left: Math.max(8, Math.min(left, vw - tooltipRect.width - 8)),
    actual: preferred,
  };
}

function getArrowStyles(position: TooltipPosition): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
  };

  switch (position) {
    case 'top':
      return {
        ...base,
        bottom: -ARROW_SIZE,
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid transparent`,
        borderTop: `${ARROW_SIZE}px solid rgb(63, 63, 70)`, // zinc-700
      };
    case 'bottom':
      return {
        ...base,
        top: -ARROW_SIZE,
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid rgb(63, 63, 70)`,
      };
    case 'left':
      return {
        ...base,
        right: -ARROW_SIZE,
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid transparent`,
        borderLeft: `${ARROW_SIZE}px solid rgb(63, 63, 70)`,
      };
    case 'right':
      return {
        ...base,
        left: -ARROW_SIZE,
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: `${ARROW_SIZE}px solid transparent`,
        borderBottom: `${ARROW_SIZE}px solid transparent`,
        borderRight: `${ARROW_SIZE}px solid rgb(63, 63, 70)`,
      };
  }
}

const motionVariants = {
  top: {
    initial: { opacity: 0, y: 4, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 4, scale: 0.97 },
  },
  bottom: {
    initial: { opacity: 0, y: -4, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -4, scale: 0.97 },
  },
  left: {
    initial: { opacity: 0, x: 4, scale: 0.97 },
    animate: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: 4, scale: 0.97 },
  },
  right: {
    initial: { opacity: 0, x: -4, scale: 0.97 },
    animate: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: -4, scale: 0.97 },
  },
} as const;

export function Tooltip({
  content,
  children,
  position = 'top',
  delay = 200,
  maxWidth = 280,
  className,
  disabled = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; actual: TooltipPosition } | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const tooltipId = useId();

  const show = useCallback(() => {
    if (disabled) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
  }, [disabled, delay]);

  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsVisible(false);
  }, []);

  const toggle = useCallback(() => {
    if (disabled) return;
    setIsVisible(prev => !prev);
  }, [disabled]);

  // Position calculation
  useEffect(() => {
    if (!isVisible || !triggerRef.current) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      // Estimate tooltip size for initial render, then refine
      const tooltipEl = tooltipRef.current;
      const tooltipRect = tooltipEl
        ? { width: tooltipEl.offsetWidth, height: tooltipEl.offsetHeight }
        : { width: maxWidth, height: 60 };
      setCoords(getTooltipPosition(triggerRect, tooltipRect, position));
    };

    // Initial position
    updatePosition();
    // Refine after tooltip renders
    const raf = requestAnimationFrame(updatePosition);

    return () => cancelAnimationFrame(raf);
  }, [isVisible, position, maxWidth]);

  // Click outside to close (mobile)
  useEffect(() => {
    if (!isVisible) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        tooltipRef.current && !tooltipRef.current.contains(e.target as Node)
      ) {
        setIsVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible]);

  // Escape key to close
  useEffect(() => {
    if (!isVisible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  // Cleanup timeout on unmount
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  if (!isValidElement(children)) return children;

  const child = cloneElement(children as React.ReactElement<Record<string, unknown>>, {
    ref: triggerRef,
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      const original = (children.props as Record<string, unknown>).onMouseEnter;
      if (typeof original === 'function') (original as (e: React.MouseEvent) => void)(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      const original = (children.props as Record<string, unknown>).onMouseLeave;
      if (typeof original === 'function') (original as (e: React.MouseEvent) => void)(e);
    },
    onClick: (e: React.MouseEvent) => {
      toggle();
      const original = (children.props as Record<string, unknown>).onClick;
      if (typeof original === 'function') (original as (e: React.MouseEvent) => void)(e);
    },
    'aria-describedby': isVisible ? tooltipId : undefined,
  });

  const actualPos = coords?.actual ?? position;

  return (
    <>
      {child}
      {createPortal(
        <AnimatePresence>
          {isVisible && coords && (
            <motion.div
              ref={tooltipRef}
              id={tooltipId}
              role="tooltip"
              initial={motionVariants[actualPos].initial}
              animate={motionVariants[actualPos].animate}
              exit={motionVariants[actualPos].exit}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className={cn(
                'fixed z-[9999] px-3 py-2 bg-surface-light border border-border rounded-lg shadow-xl',
                'text-xs text-text-primary leading-relaxed',
                className
              )}
              style={{
                top: coords.top - window.scrollY,
                left: coords.left,
                maxWidth,
                pointerEvents: 'none',
              }}
            >
              <div style={getArrowStyles(actualPos)} />
              {content}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

/** InfoTooltip - Info icon that shows a tooltip on hover/click */
export function InfoTooltip({ content, position = 'top', size = 'sm', className }: InfoTooltipProps) {
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <Tooltip content={content} position={position}>
      <span className={cn('inline-flex items-center cursor-help', className)}>
        <Info className={cn(iconSize, 'text-text-muted hover:text-text-primary transition-colors')} />
      </span>
    </Tooltip>
  );
}
